import axios from 'axios';
import { getApiBaseUrl } from '../utils/config';

// Token管理
const TOKEN_KEY = 'access_token';
const USER_KEY = 'current_user';

// 动态获取API配置
const API_BASE_URL = getApiBaseUrl();

// 创建独立的axios实例用于认证
const authClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  }
});

/**
 * 认证服务类
 */
class AuthService {
  constructor() {
    this.token = this.getStoredToken();
    this.user = this.getStoredUser();
    this.setupRequestInterceptor();
  }

  /**
   * 设置请求拦截器，自动添加认证头
   */
  setupRequestInterceptor() {
    authClient.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * 用户登录
   */
  async login(email, password) {
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await authClient.post('/api/v1/auth/jwt/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token, token_type } = response.data;
      
      if (access_token) {
        this.setToken(access_token);
        
        // 获取用户信息
        const userInfo = await this.getCurrentUser();
        this.setUser(userInfo);

        // 触发登录成功事件
        window.dispatchEvent(new CustomEvent('auth-login', { 
          detail: { user: userInfo } 
        }));

        return { success: true, user: userInfo };
      }
    } catch (error) {
      console.error('登录失败:', error);
      const message = error.response?.data?.detail || '登录失败，请检查用户名和密码';
      return { success: false, error: message };
    }
  }

  /**
   * 用户注册
   */
  async register(userData) {
    try {
      const response = await authClient.post('/api/v1/auth/register', userData);
      
      // 注册成功后自动登录
      if (response.data?.email) {
        const loginResult = await this.login(userData.email, userData.password);
        return { 
          success: true, 
          user: response.data,
          autoLogin: loginResult.success 
        };
      }
    } catch (error) {
      console.error('注册失败:', error);
      const message = error.response?.data?.detail || '注册失败，请稍后重试';
      return { success: false, error: message };
    }
  }

  /**
   * 用户登出
   */
  async logout() {
    try {
      // 调用后端登出接口
      if (this.token) {
        await authClient.post('/api/v1/auth/jwt/logout');
      }
    } catch (error) {
      console.error('登出请求失败:', error);
    } finally {
      // 清除本地认证信息
      this.clearAuthData();
      
      // 触发登出事件
      window.dispatchEvent(new CustomEvent('auth-logout', { 
        detail: { reason: 'user_logout' } 
      }));
    }
  }

  /**
   * 获取当前用户信息
   */
  async getCurrentUser() {
    try {
      const response = await authClient.get('/api/v1/users/me');
      const user = response.data;
      this.setUser(user);
      return user;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      throw error;
    }
  }

  /**
   * 更新用户信息
   */
  async updateUser(userData) {
    try {
      const response = await authClient.patch('/api/v1/users/me', userData);
      const updatedUser = response.data;
      this.setUser(updatedUser);
      
      // 触发用户信息更新事件
      window.dispatchEvent(new CustomEvent('user-updated', { 
        detail: { user: updatedUser } 
      }));
      
      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('更新用户信息失败:', error);
      const message = error.response?.data?.detail || '更新失败，请稍后重试';
      return { success: false, error: message };
    }
  }

  /**
   * 获取用户详细资料
   */
  async getUserProfile() {
    try {
      const response = await authClient.get('/api/v1/users/me/profile');
      return response.data;
    } catch (error) {
      console.error('获取用户详细资料失败:', error);
      throw error;
    }
  }

  /**
   * 忘记密码
   */
  async forgotPassword(email) {
    try {
      await authClient.post('/api/v1/auth/forgot-password', { email });
      return { success: true };
    } catch (error) {
      console.error('发送重置密码邮件失败:', error);
      const message = error.response?.data?.detail || '发送失败，请稍后重试';
      return { success: false, error: message };
    }
  }

  /**
   * 重置密码
   */
  async resetPassword(token, password) {
    try {
      await authClient.post('/api/v1/auth/reset-password', { token, password });
      return { success: true };
    } catch (error) {
      console.error('重置密码失败:', error);
      const message = error.response?.data?.detail || '重置失败，请稍后重试';
      return { success: false, error: message };
    }
  }

  /**
   * 请求邮箱验证
   */
  async requestVerification() {
    try {
      await authClient.post('/api/v1/auth/request-verify-token');
      return { success: true };
    } catch (error) {
      console.error('请求邮箱验证失败:', error);
      const message = error.response?.data?.detail || '请求失败，请稍后重试';
      return { success: false, error: message };
    }
  }

  /**
   * 验证邮箱
   */
  async verifyEmail(token) {
    try {
      await authClient.post('/api/v1/auth/verify', { token });
      // 重新获取用户信息更新验证状态
      await this.getCurrentUser();
      return { success: true };
    } catch (error) {
      console.error('邮箱验证失败:', error);
      const message = error.response?.data?.detail || '验证失败，请检查验证链接';
      return { success: false, error: message };
    }
  }

  /**
   * 获取微信登录授权URL
   */
  async getWeChatAuthUrl(state = null) {
    try {
      const params = state ? { state } : {};
      const response = await authClient.get('/api/v1/auth/wechat/url', { params });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('获取微信授权URL失败:', error);
      const message = error.response?.data?.detail || '获取微信授权URL失败';
      return { success: false, error: message };
    }
  }

  /**
   * 微信登录
   */
  async wechatLogin(code, state = null) {
    try {
      const params = { code };
      if (state) params.state = state;
      
      const response = await authClient.post('/api/v1/auth/wechat/login', null, { params });
      const { access_token, user, is_new_user } = response.data;
      
      if (access_token) {
        this.setToken(access_token);
        this.setUser(user);

        // 触发登录成功事件
        window.dispatchEvent(new CustomEvent('auth-login', { 
          detail: { user, isNewUser: is_new_user } 
        }));

        return { success: true, user, isNewUser: is_new_user };
      }
    } catch (error) {
      console.error('微信登录失败:', error);
      const message = error.response?.data?.detail || '微信登录失败，请重试';
      return { success: false, error: message };
    }
  }

  /**
   * 绑定一个新的认证方式
   */
  async bindAccount(payload) {
    try {
      const response = await authClient.post('/api/v1/users/me/bind', payload);
      // 绑定成功后，重新获取最新的用户资料
      await this.getUserProfile();
      await this.getCurrentUser();
      return { success: true, data: response.data };
    } catch (error) {
      console.error('账号绑定失败:', error);
      const message = error.response?.data?.detail || '账号绑定失败';
      return { success: false, error: message };
    }
  }

  /**
   * 处理微信登录回调
   */
  async handleWeChatCallback(code, state = null) {
    try {
      const response = await authClient.get('/api/v1/auth/wechat/callback', {
        params: { code, state }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('处理微信回调失败:', error);
      const message = error.response?.data?.detail || '处理微信回调失败';
      return { success: false, error: message };
    }
  }

  // Token管理方法
  getToken() {
    return this.token;
  }

  setToken(token) {
    this.token = token;
    // 简单的混淆处理，增加XSS攻击难度
    const encodedToken = btoa(token + '|' + Date.now());
    localStorage.setItem(TOKEN_KEY, encodedToken);
  }

  getStoredToken() {
    const encodedToken = localStorage.getItem(TOKEN_KEY);
    if (!encodedToken) return null;
    
    try {
      const decoded = atob(encodedToken);
      const parts = decoded.split('|');
      if (parts.length >= 2) {
        const token = parts[0];
        const timestamp = parseInt(parts[1]);
        
        // 检查token是否过期（7天本地存储限制）
        const maxAge = 7 * 24 * 60 * 60 * 1000; // 7天
        if (Date.now() - timestamp > maxAge) {
          this.clearAuthData();
          return null;
        }
        
        return token;
      }
    } catch (error) {
      console.error('Token解码失败:', error);
      this.clearAuthData();
    }
    
    return null;
  }

  // 用户信息管理方法
  getUser() {
    return this.user;
  }

  setUser(user) {
    this.user = user;
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  getStoredUser() {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('解析存储的用户信息失败:', error);
      return null;
    }
  }

  // 清除认证数据
  clearAuthData() {
    this.token = null;
    this.user = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  // 检查是否已登录
  isAuthenticated() {
    return !!this.token && !!this.user;
  }

  // 检查用户是否已验证邮箱
  isEmailVerified() {
    return this.user?.is_verified === true;
  }

  // 检查用户是否为管理员
  isSuperUser() {
    return this.user?.is_superuser === true;
  }

  // 获取用户角色
  getUserRole() {
    return this.user?.role || 'user';
  }
}

// 创建单例实例
const authService = new AuthService();

export default authService; 